import { z } from 'zod';
import { AuditMeta, DocId, TenantId, UserRole } from '../common.js';
import { Iban } from './declaratie.js';
import { SaraSoepStyle } from './sara-task.js';
import { MeldingVoorkeuren } from './notification.js';
/**
 * A user belongs to a tenant. SSO-authenticated users are keyed by their
 * Microsoft Entra subject (sub claim); dev-token users use synthetic IDs.
 *
 * Firestore path: tenants/{tenantId}/users/{userId}
 */
export const UserSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    email: z.string().email().toLowerCase(),
    displayName: z.string().min(1).max(200),
    role: UserRole,
    /**
     * Microsoft Entra object ID (aka oid claim). Stable per user across sessions.
     * Undefined for dev-token users.
     */
    entraOid: z.string().uuid().optional(),
    status: z.enum(['active', 'invited', 'deactivated']),
    /**
     * Last completed OAuth round-trip (Entra callback success). Updates ONLY
     * when the user goes through Microsoft sign-in fresh — so a user with
     * a valid 90-day session cookie can have `lastLoginAt` weeks in the past
     * while still actively using the app every day. Use `lastActiveAt` for
     * "last seen" reporting.
     */
    lastLoginAt: z.string().datetime({ offset: true }).optional(),
    /**
     * Last authenticated request — touched (throttled to 1h granularity) by
     * the `authenticate` middleware on every successful session-cookie auth.
     * This is the field analytics dashboards should use for "active users
     * in last N days" / "who hasn't logged in in 30+ days" type queries.
     */
    lastActiveAt: z.string().datetime({ offset: true }).optional(),
    /**
     * NL-datum (YYYY-MM-DD) waarop deze gebruiker voor het laatst in
     * `activiteit_dagen` is geteld. Los van lastActiveAt, omdat de OAuth-login
     * die ook zet en de eerste dag dan ongeteld zou blijven.
     */
    activiteitDag: z.string().optional(),
    /**
     * Head-office membership flag — narrower than `isInternalRole(role)`.
     * Computed at every OAuth login from the user's Entra group claim
     * (true iff the user is in the `Kantoorpersoneel` group whose GUID is
     * configured via env.ENTRA_GROUP_KANTOORPERSONEEL). Gates access to
     * the `kennisbank_ring_hq` corpus — internal process + policy docs
     * that even regular SUPPOHRT medewerkers should not see.
     *
     * Optional + defaults-to-false so legacy User records without the
     * field don't accidentally gain HQ access. Re-read on every login,
     * so adding/removing a user from Kantoorpersoneel takes effect on
     * their next Entra sign-in (not the next session-cookie use).
     */
    isHeadOffice: z.boolean().optional(),
    /**
     * AI-tools (Mila én Sara, samen: Sara hangt aan Mila) aan voor deze
     * gebruiker. Standaard uit: de brede uitrol naar alle POH-GGZ (besluit Jos
     * 3 sep 2026) geeft iedereen de SharePoint-vervanger, en kantoor zet de
     * AI-tools per persoon aan in Beheer Gebruikers. Bewust geen Entra-groep:
     * dit is een uitrolvlag die zonder IT-beheer moet kunnen schuiven.
     */
    aiTools: z.boolean().optional(),
    /**
     * Mag deze gebruiker naar tenant `rhogo` wisselen. Alleen gezet door de
     * OAuth-callback uit ENTRA_GROUP_RHOGO_WISSELEN / RHOGO_WISSELEN_ENTRA_OIDS,
     * bij elke login opnieuw; geen route of beheerscherm schrijft dit veld.
     */
    magTenantWisselen: z.boolean().optional(),
    /** Rol in RHOGO bij het wisselen, gezet naast magTenantWisselen (combinatierol). */
    rhogoWisselRol: z.enum(['zorggroep_admin', 'zorggroep_gebruiker']).optional(),
    /**
     * Alleen op het RHOGO-account van een wisselaar: de thuistenant waar dit
     * account bij hoort. Markeert het record als wisselaccount (geen eigen
     * entra_oid_index-pointer; inloggen landt altijd in de thuistenant).
     */
    wisselVanuitTenantId: TenantId.optional(),
    /** Niet tonen in Smoelenboek en aanwezigheid (RHOGO_VERBORGEN_ENTRA_OIDS). */
    verborgenInSmoelenboek: z.boolean().optional(),
    /** Wanneer de gebruiker zelf om AI-tools vroeg; gewist zodra het vinkje aan gaat. */
    aiToolsRequestedAt: z.string().datetime({ offset: true }).nullish(),
    /**
     * Wie de AI-tools aanzette, als weergavenaam. De audit-log houdt het
     * user-id bij, maar dat is onvindbaar vanuit Beheer Gebruikers; zonder deze
     * twee velden kan een beheerder achteraf niet zien of zij het zelf deed
     * (verzoek Jos 17 sep 2026). Leeg zodra de tools weer uit gaan.
     */
    aiToolsBy: z.string().max(200).nullish(),
    aiToolsAt: z.string().datetime({ offset: true }).nullish(),
    /**
     * Entra security-group object-IDs (GUIDs) uit de `groups`-claim, bewaard bij
     * elke OAuth-login. Nodig om de Intervisie-pagina (#25) per gebruiker te
     * filteren op groepslidmaatschap ZONDER een Microsoft Graph-call: de backend
     * zet de op de intervisiegroep geconfigureerde `entraGroupId` af tegen deze
     * lijst. Optioneel + re-read bij elke login (zelfde patroon als isHeadOffice),
     * zodat een groepswijziging in Entra bij de eerstvolgende sign-in doorwerkt.
     * De Enterprise-app levert alleen app-relevante groepen mee, dus deze lijst
     * blijft klein.
     */
    entraGroupIds: z.array(z.string()).max(200).optional(),
    /**
     * GCS object path (NOT a URL) of the profile photo, e.g. `<entraOid>.jpg`,
     * stored in the private PROFILE_PHOTOS_BUCKET. The bucket is private (no
     * public read) per AVG-besluit 2026-06-22 — practitioner photos are personal
     * data and must not be reachable without auth. The browser-displayable URL is
     * a short-lived signed read URL minted at serve time (see image-url.service).
     */
    photoObject: z.string().max(300).optional(),
    /**
     * Zelfgekozen weergavenaam die de Entra-`displayName` vervangt in het
     * Smoelenboek. Leeg (of afwezig) = toon de Entra-naam. Lege string is
     * toegestaan zodat wissen werkt: Firestore-merge laat `undefined` vallen.
     */
    weergavenaam: z.string().max(200).optional(),
    /** Afwezig = standaardwaarden (MELDING_VOORKEUREN_STANDAARD). */
    meldingen: MeldingVoorkeuren.optional(),
    specialisaties: z.array(z.string().min(1).max(60)).max(10).optional(),
    regio: z.string().max(100).optional(),
    locatie: z.string().max(200).optional(),
    /** Vrij "Over mij"-veld (5-6 zinnen). Enige echt vrije tekst in het profiel naast opleiding/ervaring. */
    biografie: z.string().max(1000).optional(),
    /** Opleiding + eerdere werkvelden, vrije tekst. */
    opleidingErvaring: z.string().max(1000).optional(),
    /** POH-GGZ sinds — ISO-datum (YYYY-MM-DD) uit de date-picker. */
    pohGgzSinds: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /**
     * Functietitel voor kantoorpersoneel (isHeadOffice), vrije tekst. Vervangt in
     * het Smoelenboek het rollabel "POH-GGZ"; POH-GGZ vult dit niet in.
     * Lege string = gewist (zelfde merge-reden als weergavenaam).
     */
    functietitel: z.string().max(100).optional(),
    /**
     * Zelfgekozen profieltype voor admins (besluit Jos 3 sep 2026): admins zijn
     * standaard kantoor (isHeadOffice) maar kunnen ook POH-GGZ zijn. Afwezig =
     * afleiden uit isHeadOffice. Alleen suppohrt_admin mag dit zetten.
     */
    profielType: z.enum(['poh_ggz', 'kantoor']).optional(),
    /** Optioneel telefoonnummer (facultatief intern delen). */
    telefoon: z.string().max(30).optional(),
    /**
     * Registratienummers voor accreditatie van scholing. Optioneel: niet elke
     * gebruiker is lid. Eenmalig hier ingevuld, daarna automatisch meegekopieerd
     * naar elke agenda-aanmelding (zie AgendaRegistrationSchema) zodat kantoor de
     * punten kan bijschrijven zonder ze telkens opnieuw uit te vragen.
     */
    lidnummerLvpoh: z.string().max(50).optional(),
    lidnummerSkj: z.string().max(50).optional(),
    /**
     * Gewenste SOEP-schrijfstijl voor Sara (besluit 20-8-2026). Gezet vanuit de
     * dropdown op de Sara-pagina (via PUT /profile); afwezig = 'bondig'.
     */
    soepStyle: SaraSoepStyle.optional(),
    /**
     * IBAN voor kostendeclaraties (besluit Jos 20 aug 2026: eenmalig in het
     * profiel, voorgevuld op elke declaratie). Zelf beheerd via PUT /profile;
     * gemaskeerd overal behalve in het eigen profiel en de declaratiedetail.
     * `null` betekent "gewist": een merge-write kan een veld niet weghalen, en
     * bank-PII moet de gebruiker zelf kunnen terugtrekken (zelfde patroon als
     * teamleiderUserId hieronder).
     */
    rekeningnummer: Iban.nullish(),
    /**
     * Woonplaats voor kostendeclaraties: Exact vereist hem bij uitbetalen
     * (Jos 26 okt 2026). Zelfde patroon als het IBAN: privé, alleen in het
     * eigen profiel en op de declaratie. In het Smoelenboek alleen als de
     * gebruiker `woonplaatsZichtbaar` zelf aanzet (opt-in, Jos 26 okt 2026).
     */
    woonplaats: z.string().max(100).optional(),
    woonplaatsZichtbaar: z.boolean().optional(),
    /**
     * De teamleider die de declaraties van deze gebruiker als eerste
     * akkoordeert. Toegewezen door suppohrt_admin (Linda) via
     * /beheer/declaraties/teamleiders — bewust géén Entra-groep: de
     * toewijzing is per persoon en moet zonder IT-beheer aanpasbaar zijn.
     * `null` betekent "toewijzing verwijderd" — een merge-write kan een veld
     * niet wissen, dus leegmaken schrijft null in plaats van het veld weg te
     * halen.
     */
    teamleiderUserId: DocId.nullish(),
    /**
     * Markering "is teamleider" (besluit Jos 3 sep 2026): kantoor/admin vinkt in
     * Beheer Declaraties aan wie teamleider is; alleen gemarkeerde collega's
     * zijn kiesbaar in de teamleiderkeuze op de declaratiepagina. Los van
     * `teamleiderUserId` (wie MIJN teamleider is) en bewust geen Entra-groep:
     * de markering moet zonder IT-beheer aanpasbaar zijn.
     */
    isTeamleider: z.boolean().optional(),
    /**
     * TimeChimp-koppeling (besluit Jos 3 sep 2026). Afwezig = `onbepaald`: de
     * uren-routes proberen dan de e-mailmatch en leggen het gevonden id hier
     * vast, zodat een latere e-mailwijziging in Entra of TimeChimp de koppeling
     * niet meer breekt. `geen` is een bewuste beheerkeuze (deze medewerker
     * schrijft geen uren via de kennisbank) en verbergt het Uren-menu; een
     * mislukte e-mailmatch doet dat NIET, anders verdwijnt de enige melder van
     * het probleem. `userId` is het TimeChimp-user-id, nooit een gespiegelde
     * naam of e-mail: TimeChimp blijft de bron van waarheid.
     * `null` = terug naar onbepaald (een merge-write kan een veld niet wissen).
     */
    timechimp: z
        .object({
        state: z.enum(['gekoppeld', 'geen']),
        userId: z.number().int().positive().optional(),
        /** userId van de beheerder die koppelde, of 'automatisch' bij de e-mailmatch. */
        door: z.string().max(200).optional(),
        op: z.string().datetime({ offset: true }).optional(),
    })
        .nullish(),
    profileCompletedAt: z.string().datetime({ offset: true }).optional(),
});
/**
 * Self-service profile update. `photoObject` is the GCS object path returned by
 * the photo-upload-url endpoint — never a public URL (see UserSchema.photoObject).
 */
export const UpdateProfileInput = z.object({
    photoObject: z.string().max(300).optional(),
    weergavenaam: z.string().max(200).optional(),
    // Canonical membership is enforced in the route (so the "vraag beheerder"
    // message can surface); the schema only bounds shape/length here.
    specialisaties: z.array(z.string().min(1).max(60)).max(10).optional(),
    regio: z.string().max(100).optional(),
    locatie: z.string().max(200).optional(),
    biografie: z.string().max(1000).optional(),
    opleidingErvaring: z.string().max(1000).optional(),
    // Lege string = wissen (zie UserService.updateProfile), vandaar de optionele
    // datum in de regex; het formulier stuurt dit veld altijd mee.
    pohGgzSinds: z
        .string()
        .regex(/^(\d{4}-\d{2}-\d{2})?$/)
        .optional(),
    /**
     * Functietitel voor kantoorpersoneel (isHeadOffice), vrije tekst. Vervangt in
     * het Smoelenboek het rollabel "POH-GGZ"; POH-GGZ vult dit niet in.
     * Lege string = gewist (zelfde merge-reden als weergavenaam).
     */
    functietitel: z.string().trim().max(100).optional(),
    profielType: z.enum(['poh_ggz', 'kantoor']).optional(),
    telefoon: z.string().max(30).optional(),
    lidnummerLvpoh: z.string().max(50).optional(),
    lidnummerSkj: z.string().max(50).optional(),
    soepStyle: SaraSoepStyle.optional(),
    meldingen: MeldingVoorkeuren.optional(),
    // null = wissen; weglaten = ongewijzigd laten (merge-write semantiek).
    rekeningnummer: Iban.nullish(),
    // Lege string = wissen (zie UserService.updateProfile).
    woonplaats: z.string().trim().max(100).optional(),
    woonplaatsZichtbaar: z.boolean().optional(),
});
/**
 * Public projection used in the Smoelenboek listing — no audit fields or
 * internal auth data. Only active SUPPOHRT-intern users are included.
 *
 * `photoUrl` here is the DISPLAY url (a short-lived signed read URL), built by
 * the service from the stored `photoObject` — not the persisted field. Defined
 * explicitly (not via UserSchema.pick) precisely because the stored shape
 * (photoObject) and the wire shape (photoUrl) differ.
 */
export const SmoelenboekEntry = z.object({
    id: DocId,
    displayName: z.string().min(1).max(200),
    email: z.string().email(),
    role: UserRole,
    photoUrl: z.string().url().optional(),
    specialisaties: z.array(z.string().min(1).max(60)).max(10).optional(),
    regio: z.string().max(100).optional(),
    locatie: z.string().max(200).optional(),
    biografie: z.string().max(1000).optional(),
    opleidingErvaring: z.string().max(1000).optional(),
    pohGgzSinds: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /**
     * Functietitel voor kantoorpersoneel (isHeadOffice), vrije tekst. Vervangt in
     * het Smoelenboek het rollabel "POH-GGZ"; POH-GGZ vult dit niet in.
     * Lege string = gewist (zelfde merge-reden als weergavenaam).
     */
    functietitel: z.string().max(100).optional(),
    telefoon: z.string().max(30).optional(),
    /** Alleen gevuld als de gebruiker woonplaatsZichtbaar heeft aangezet. */
    woonplaats: z.string().max(100).optional(),
    profileCompletedAt: z.string().datetime({ offset: true }).optional(),
});
export const InviteUserInput = z.object({
    email: z.string().email().toLowerCase(),
    displayName: z.string().min(1).max(200),
    role: UserRole,
});
/**
 * Safe public projection for /api/me responses — never includes audit fields
 * or cross-tenant data.
 */
export const PublicUserProjection = UserSchema.pick({
    id: true,
    tenantId: true,
    email: true,
    displayName: true,
    role: true,
    status: true,
    // Waarmee de frontend beheer-knoppen (agenda) verbergt die de backend toch
    // zou weigeren. Geen geheim: het zegt alleen of je bij kantoor hoort.
    isHeadOffice: true,
    // Opent Beheer Declaraties voor teamleiders (sidebar + paginapoort).
    isTeamleider: true,
    // Stuurt de Mila/Sara-poort in de frontend; de backend weigert hoe dan ook.
    aiTools: true,
});
/**
 * Beheer Gebruikers: weergavenaam zetten namens een gebruiker. Lege string =
 * terug naar de Entra-naam (zelfde merge-semantiek als in het profiel).
 */
export const ZetWeergavenaamInput = z.object({
    weergavenaam: z.string().trim().max(200),
});
/** Beheer Gebruikers: AI-tools per gebruiker aan- of uitzetten. */
export const ZetAiToolsInput = z.object({
    aiTools: z.boolean(),
});
/** Eén rij in Beheer Gebruikers. Geen profiel- of audit-velden. */
export const BeheerGebruikerRij = z.object({
    id: DocId,
    displayName: z.string(),
    email: z.string().email(),
    role: UserRole,
    status: z.enum(['active', 'invited', 'deactivated']),
    /** Entra-naam, zodat het scherm kan tonen waar een lege weergavenaam op terugvalt. */
    entraDisplayName: z.string(),
    isHeadOffice: z.boolean(),
    isTeamleider: z.boolean(),
    aiTools: z.boolean(),
    aiToolsRequestedAt: z.string().datetime({ offset: true }).optional(),
    /** Weergavenaam van de beheerder die de AI-tools aanzette, plus wanneer. */
    aiToolsBy: z.string().optional(),
    aiToolsAt: z.string().datetime({ offset: true }).optional(),
    lastActiveAt: z.string().datetime({ offset: true }).optional(),
});
/** Unieke gebruikers per ISO-week, uit `activiteit_dagen` (verzoek Jos 26 okt 2026). */
export const GebruikWeekRij = z.object({
    week: z.string(),
    totaal: z.number().int(),
    medewerkers: z.number().int(),
    beheerders: z.number().int(),
    overig: z.number().int(),
});
//# sourceMappingURL=user.js.map